import { useState, useEffect } from "react";
import { Modal } from "./Modal";
import { FormInput } from "./FormFields";
import { api } from "../services/api";
import type {
  PermissionLevel,
  CollectionInviteLink,
} from "@poi-planner/shared";

function buildCollectionInviteUrl(token: string): string {
  return `${window.location.origin}/invite/collection/${token}`;
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

interface ShareCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  collectionId: string;
  collectionName: string;
  isOwner: boolean;
}

function formatExpiryTime(expiresAt: string): string {
  const now = new Date();
  const expiry = new Date(expiresAt);
  const diffMs = expiry.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return "Expired";
  if (diffDays === 1) return "Expires tomorrow";
  if (diffDays < 7) return `Expires in ${diffDays} days`;
  return `Expires ${expiry.toLocaleDateString()}`;
}

export function ShareCollectionModal({
  isOpen,
  onClose,
  collectionId,
  collectionName,
  isOwner,
}: ShareCollectionModalProps) {
  const [inviteLinks, setInviteLinks] = useState<CollectionInviteLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [permission, setPermission] = useState<PermissionLevel>("editor");
  const [label, setLabel] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createdLink, setCreatedLink] = useState<string | null>(null);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);

  const pendingLinks = inviteLinks.filter((link) => link.status === "pending");

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && collectionId) {
      setLoading(true);
      setPermission("editor");
      setLabel("");
      setError(null);
      setCreatedLink(null);
      setCopiedLinkId(null);

      api
        .getCollectionInviteLinks(collectionId)
        .then(setInviteLinks)
        .catch((err) => setError(err.message))
        .finally(() => setLoading(false));
    }
  }, [isOpen, collectionId]);

  const handleCreateLink = async () => {
    setError(null);
    setIsCreating(true);
    setCreatedLink(null);

    try {
      const link = await api.createCollectionInviteLink(
        collectionId,
        permission,
        label.trim() || undefined,
      );
      const url = buildCollectionInviteUrl(link.token);
      setCreatedLink(url);
      setInviteLinks([link, ...inviteLinks]);
      setLabel("");
    } catch (err: unknown) {
      setError(getErrorMessage(err, "Failed to create invite link"));
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyLink = async (linkUrl: string, linkId?: string) => {
    try {
      await navigator.clipboard.writeText(linkUrl);
      if (linkId) {
        setCopiedLinkId(linkId);
        setTimeout(() => setCopiedLinkId(null), 2000);
      }
    } catch (err) {
      console.error("Failed to copy link:", err);
    }
  };

  const handleCancelLink = async (linkId: string) => {
    try {
      await api.cancelCollectionInviteLink(linkId);
      setInviteLinks(
        inviteLinks.map((l) =>
          l.id === linkId ? { ...l, status: "cancelled" as const } : l,
        ),
      );
    } catch (err) {
      console.error("Failed to cancel link:", err);
    }
  };

  if (!isOwner) {
    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        title="Share Collection"
        icon="share"
        size="lg"
      >
        <div className="text-center py-8 text-secondary">
          Only the collection owner can manage sharing settings.
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={`Share "${collectionName}"`}
      icon="share"
      size="xl"
    >
      <div className="flex flex-col gap-6">
        {/* Create Invite Link Section */}
        <div>
          <div className="flex items-center justify-between mb-4 pb-3 border-b-2 border-amber-500">
            <div>
              <h4 className="text-xs font-bold text-primary uppercase tracking-wide">
                Create Invite Link
              </h4>
              <p className="text-xs text-secondary mt-0.5">
                Generate a shareable link for anyone to join
              </p>
            </div>
            <span className="material-symbols-outlined text-xl text-amber-500">
              link
            </span>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex gap-3">
              <div className="flex-1">
                <label className="block text-xs font-semibold text-secondary uppercase tracking-wide mb-1.5">
                  Permission
                </label>
                <select
                  value={permission}
                  onChange={(e) =>
                    setPermission(e.target.value as PermissionLevel)
                  }
                  className="w-full px-3 py-2.5 bg-subtle border border-border rounded-lg text-sm text-primary outline-none focus:border-amber-500"
                >
                  <option value="editor">Editor - Can add/remove POIs</option>
                  <option value="viewer">Viewer - Can only view</option>
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-xs font-semibold text-secondary uppercase tracking-wide mb-1.5">
                  Label (optional)
                </label>
                <FormInput
                  value={label}
                  onChange={setLabel}
                  placeholder="e.g., For mom"
                />
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-error-bg border border-error/25 rounded-lg text-sm text-error">
                <span className="material-symbols-outlined text-lg">error</span>
                <span>{error}</span>
              </div>
            )}

            {createdLink && (
              <div className="p-4 bg-gradient-to-br from-success-bg to-success-bg border border-success/25 rounded-xl">
                <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-success">
                  <span className="material-symbols-outlined text-lg text-success">
                    check_circle
                  </span>
                  Link created! Share it with your collaborator:
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={createdLink}
                    readOnly
                    className="flex-1 px-3 py-2 bg-white border border-success/25 rounded-lg text-xs font-mono text-primary outline-none"
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                  <button
                    onClick={() => handleCopyLink(createdLink)}
                    className="flex items-center gap-2 px-4 py-2 bg-success text-white border-none rounded-lg text-xs font-semibold cursor-pointer hover:bg-success/90 transition-colors"
                  >
                    <span className="material-symbols-outlined text-base">
                      content_copy
                    </span>
                    Copy
                  </button>
                </div>
              </div>
            )}

            <button
              onClick={handleCreateLink}
              disabled={isCreating}
              className="flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white border-none rounded-xl text-sm font-bold cursor-pointer hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg shadow-amber-500/20 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isCreating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Creating...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-lg">
                    add_link
                  </span>
                  Create Link
                </>
              )}
            </button>
          </div>
        </div>

        {/* Active Links */}
        {pendingLinks.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-border">
              <h4 className="text-xs font-bold text-primary uppercase tracking-wide">
                Active Links
              </h4>
              <span className="px-2 py-0.5 bg-amber-100 text-amber-600 rounded-full text-xs font-bold">
                {pendingLinks.length}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              {pendingLinks.map((link) => {
                const linkUrl = buildCollectionInviteUrl(link.token);
                const isCopied = copiedLinkId === link.id;

                return (
                  <div
                    key={link.id}
                    className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg hover:border-amber-300 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center size-9 bg-gradient-to-br from-amber-100 to-orange-100 rounded-lg">
                        <span className="material-symbols-outlined text-lg text-amber-600">
                          link
                        </span>
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-primary">
                          {link.label ||
                            `${link.permissionLevel === "editor" ? "Editor" : "Viewer"} link`}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${
                              link.permissionLevel === "editor"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-purple-100 text-purple-700"
                            }`}
                          >
                            {link.permissionLevel}
                          </span>
                          <span className="text-xs text-tertiary">
                            {formatExpiryTime(link.expiresAt)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleCopyLink(linkUrl, link.id)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs font-semibold cursor-pointer transition-all ${
                          isCopied
                            ? "bg-success-bg border-success/25 text-success"
                            : "bg-white border-slate-200 text-secondary hover:border-slate-400"
                        }`}
                      >
                        <span className="material-symbols-outlined text-sm">
                          {isCopied ? "check" : "content_copy"}
                        </span>
                        {isCopied ? "Copied!" : "Copy"}
                      </button>
                      <button
                        onClick={() => handleCancelLink(link.id)}
                        className="flex items-center justify-center size-7 bg-transparent border border-slate-200 rounded-lg text-tertiary cursor-pointer hover:bg-error-bg hover:border-error/25 hover:text-error transition-colors"
                        title="Cancel link"
                      >
                        <span className="material-symbols-outlined text-base">
                          close
                        </span>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="w-8 h-8 border-3 border-gray-200 border-t-amber-500 rounded-full animate-spin"></div>
          </div>
        )}
      </div>
    </Modal>
  );
}
